// src/components/admin/ServicesEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  getSalonServices,
  addSalonService,
  updateSalonService,
  deleteSalonService
} from '../../api/salonApi';
import { getSalonProducts } from '../../api/productsApi';
import { storage } from '../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ImageCropModal from './ImageCropModal';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/services.css';
import '../../styles/responsive-utils.css';
import '../../styles/services-responsive.css';

function ServicesEditor({ salonId, onUpdate, savedMessage, onPreview }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salonProducts, setSalonProducts] = useState([]);


  const [isEditing, setIsEditing] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);


  // Services are a global catalog — no per-location filtering needed.
  // useSalonLocations is kept only to avoid breaking other hooks/imports.
  const { loading: locationsLoading } = useSalonLocations(salonId);
  const visibleServices = services;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 30,
    priceMin: 0,
    priceMax: 0,
    category: 'Hair',
    isActive: true,
    allowConcurrentBooking: false,
    concurrentTimePercentage: 50,
    imageUrl: '',
    linkedProducts: [{ productId: '', productName: '', qty: '' }],
  });

  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    duration: '',
    priceMin: '',
    priceMax: '',
  });

  const [cropFile, setCropFile] = useState(null); // file waiting to be framed
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  // Categories for services
  const categories = ['Hair', 'Nails', 'Facial', 'Massage', 'Makeup', 'Other'];
  
  // Load services and products from Firebase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [loadedServices, loadedProducts] = await Promise.all([
          getSalonServices(salonId),
          getSalonProducts(salonId)
        ]);
        setServices(loadedServices);
        setSalonProducts(loadedProducts.filter(p => p.isActive !== false));
        setError('');
      } catch (err) {
        console.error('Error loading services:', err);
        setError('Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [salonId]);
  
  // Seeded services can carry a stored `id` field that differs from the Firestore document ID,
  // so writes must target `docId` (falls back to `id` for services added in this session).
  const getDocId = (serviceId) => services.find(s => s.id === serviceId)?.docId ?? serviceId;

  const validateField = (fieldName, value) => {
    let errorMsg = '';

    if (fieldName === 'name' && (!value || value.trim() === '')) {
      errorMsg = 'Service Name is mandatory. Please fill in this field.';
    } else if (fieldName === 'duration' && (!value || Number(value) <= 0)) {
      errorMsg = 'Duration must be greater than 0 minutes.';
    } else if (fieldName === 'priceMin' && value !== '' && value !== null && Number(value) < 0) {
      errorMsg = 'Minimum price must be 0 or greater.';
    } else if (fieldName === 'priceMax' && value !== '' && value !== null && Number(value) < 0) {
      errorMsg = 'Maximum price must be 0 or greater.';
    }

    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
    return errorMsg === '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const isPriceField = name === 'priceMin' || name === 'priceMax';
    const newValue = type === 'checkbox' ? checked :
                     isPriceField ? (value === '' ? '' : Number(value)) :
                     name === 'duration' ? Number(value) : value;

    setFormData({
      ...formData,
      [name]: newValue
    });

    // Validate mandatory fields on change
    if (name === 'name' || name === 'duration' || name === 'priceMin' || name === 'priceMax') {
      validateField(name, newValue);
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'name' || name === 'duration' || name === 'priceMin' || name === 'priceMax') {
      validateField(name, value);
    }
  };

  // Dropping or selecting a file opens the positioning dialog; the upload
  // starts once the admin confirms how the image is framed.
  const handleImageDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    setImageUploadError('');
    setCropFile(acceptedFiles[0]);
  }, []);

  const handleCropConfirm = useCallback(async (file) => {
    setCropFile(null);
    setImageUploading(true);
    setImageUploadError('');
    try {
      const timestamp = Date.now();
      const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, `salons/${salonId}/services/${safeFileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setFormData(prev => ({ ...prev, imageUrl: downloadURL }));
    } catch (err) {
      console.error('Error uploading service image:', err);
      setImageUploadError('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  }, [salonId]);

  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps, isDragActive } = useDropzone({
    onDrop: handleImageDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
    disabled: imageUploading
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImageUploadError('');
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', duration: 30, priceMin: '', priceMax: '',
      category: 'Hair', isActive: true,
      allowConcurrentBooking: false, concurrentTimePercentage: 50,
      imageUrl: '',
      linkedProducts: [{ productId: '', productName: '', qty: '' }],
    });
    setFieldErrors({ name: '', duration: '', priceMin: '', priceMax: '' });
    setImageUploadError('');
  };

  const handleAddService = () => {
    setIsEditing(true);
    setEditingServiceId(null);
    resetForm();
    setError('');
  };


  const parseLegacyPrice = (svc) => {
    if (svc.priceMin !== undefined) return { priceMin: svc.priceMin, priceMax: svc.priceMax ?? svc.priceMin };
    const raw = typeof svc.price === 'number' ? svc.price : parseFloat(svc.price) || 0;
    return { priceMin: raw, priceMax: raw };
  };


  const handleEditService = (service) => {
    setIsEditing(true);
    setEditingServiceId(service.id);
    const { priceMin, priceMax } = parseLegacyPrice(service);
    setFormData({
      name: service.name,
      description: service.description,
      duration: typeof service.duration === 'number' ? service.duration : parseInt(service.duration, 10),
      priceMin,
      priceMax,
      category: service.category,
      isActive: service.isActive,
      allowConcurrentBooking: service.allowConcurrentBooking || false,
      concurrentTimePercentage: service.concurrentTimePercentage || 50,
      imageUrl: service.imageUrl || '',
      linkedProducts: (() => {
        // Migrate legacy flat fields if new array not present
        if (Array.isArray(service.linkedProducts) && service.linkedProducts.length > 0) {
          const rows = service.linkedProducts.map(lp => ({
            productId: lp.productId || '',
            productName: lp.productName || '',
            qty: lp.qty !== undefined && lp.qty !== null && lp.qty !== '' ? String(lp.qty) : '',
          }));
          if (rows.length < 3) rows.push({ productId: '', productName: '', qty: '' });
          return rows.slice(0, 3);
        }
        if (service.linkedProductId) {
          return [
            { productId: service.linkedProductId, productName: service.linkedProductName || '', qty: service.linkedProductQty !== undefined && service.linkedProductQty !== null && service.linkedProductQty !== '' ? String(service.linkedProductQty) : '' },
            { productId: '', productName: '', qty: '' },
          ];
        }
        return [{ productId: '', productName: '', qty: '' }];
      })(),
    });
    setFieldErrors({ name: '', duration: '', priceMin: '', priceMax: '' });
    setImageUploadError('');
    setError('');
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingServiceId(null);
  };
  
  const handleSaveService = async () => {
    // Validate all mandatory fields
    const nameValid = validateField('name', formData.name);
    const durationValid = validateField('duration', formData.duration);
    const priceMinValid = validateField('priceMin', formData.priceMin);
    const priceMaxValid = validateField('priceMax', formData.priceMax);

    if (!nameValid || !durationValid || !priceMinValid || !priceMaxValid) {
      setError('Please fix the validation errors before saving.');
      return;
    }

    // Additional validation
    if (formData.duration <= 0) {
      setError('Duration must be greater than 0');
      return;
    }

    const minBlank = formData.priceMin === '' || formData.priceMin === null;
    const maxBlank = formData.priceMax === '' || formData.priceMax === null;

    if (!minBlank && !maxBlank && formData.priceMax < formData.priceMin) {
      setError('Maximum price cannot be less than minimum price.');
      return;
    }

    if (imageUploading) {
      setError('Please wait for the image to finish uploading.');
      return;
    }

    // Validate linked products
    for (let i = 0; i < formData.linkedProducts.length; i++) {
      const lp = formData.linkedProducts[i];
      if (!lp.productId) continue;
      const qty = lp.qty;
      if (qty === '' || qty === null || qty === undefined) {
        setError(`Please enter a quantity for Product ${i + 1}.`);
        return;
      }
      const qtyNum = Number(qty);
      if (isNaN(qtyNum) || qtyNum < 0 || qtyNum > 10) {
        setError(`Product ${i + 1} quantity must be between 0 and 10.`);
        return;
      }
      if (Math.round(qtyNum * 10) !== qtyNum * 10) {
        setError(`Product ${i + 1} quantity must be up to 1 decimal place.`);
        return;
      }
    }

    // Prices are optional: a blank price takes the other field's value, or 0 if both are blank
    const resolvedMin = minBlank ? (maxBlank ? 0 : Number(formData.priceMax)) : Number(formData.priceMin);
    const resolvedMax = maxBlank ? resolvedMin : Number(formData.priceMax);

    // Resolve linked products — only keep rows with a product selected
    const resolvedLinkedProducts = formData.linkedProducts
      .filter(lp => !!lp.productId)
      .map(lp => {
        const prod = salonProducts.find(p => p.id === lp.productId);
        return {
          productId: lp.productId,
          productName: prod?.name || lp.productName || '',
          qty: parseFloat(Number(lp.qty).toFixed(1)),
        };
      });

    // Firestore rejects `undefined` field values (e.g. a legacy service with no description/category)
    const { linkedProducts: _lp, linkedProductId: _id, linkedProductName: _nm, linkedProductQty: _q, ...restFormData } = formData;
    const serviceData = Object.fromEntries(
      Object.entries({ ...restFormData, priceMin: resolvedMin, priceMax: resolvedMax, linkedProducts: resolvedLinkedProducts })
        .filter(([, v]) => v !== undefined)
    );

    setLoading(true);
    setError('');

    try {
      if (editingServiceId) {
        // Update existing service
        await updateSalonService(salonId, getDocId(editingServiceId), serviceData);

        // Update local state
        setServices(services.map(service =>
          service.id === editingServiceId ?
            { ...service, ...serviceData, id: editingServiceId } :
            service
        ));
      } else {
        // Add new service
        const newServiceId = await addSalonService(salonId, serviceData);

        // Update local state
        const newService = {
          ...serviceData,
          id: newServiceId
        };
        setServices([...services, newService]);
      }
      
      // Reset form state
      setIsEditing(false);
      setEditingServiceId(null);
      
      // Call the parent component's update function if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error saving service:', err);
      const detail = err?.code || err?.message;
      setError(`Failed to save service. Please try again.${detail ? ` (${detail})` : ''}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        setLoading(true);
        
        // Delete from Firebase
        await deleteSalonService(salonId, getDocId(serviceId));
        
        // Update local state
        setServices(services.filter(service => service.id !== serviceId));
        
        // Call the parent component's update function if provided
        if (onUpdate) {
          onUpdate();
        }
      } catch (err) {
        console.error('Error deleting service:', err);
        setError('Failed to delete service. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleToggleActive = async (serviceId) => {
    try {
      // Find the service
      const service = services.find(s => s.id === serviceId);
      
      if (!service) {
        return;
      }
      
      // Toggle isActive status
      const updatedService = {
        ...service,
        isActive: !service.isActive
      };
      
      // Update in Firebase
      await updateSalonService(salonId, getDocId(serviceId), { isActive: updatedService.isActive });
      
      // Update local state
      setServices(services.map(s => 
        s.id === serviceId ? updatedService : s
      ));
      
      // Call the parent component's update function if provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error toggling service status:', err);
      setError('Failed to update service status. Please try again.');
    }
  };
  
  
  if ((loading && services.length === 0) || locationsLoading) {
    return <div className="services-editor-loading">Loading services...</div>;
  }

  // Group *visible* services by category for display
  const visibleByCategory = {};
  visibleServices.forEach(service => {
    if (!visibleByCategory[service.category]) {
      visibleByCategory[service.category] = [];
    }
    visibleByCategory[service.category].push(service);
  });

  return (
    <div className="services-editor">
      <div className="editor-page-header">
        <h2>Services</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
        </div>
      </div>
      <p className="editor-description">
        Manage your salon's service offerings, including pricing, duration, and availability.
        Services are shared across all locations — availability at each location is controlled by staff assignments.
      </p>

      {error && <div className="error-message">{error}</div>}

      {!isEditing ? (
        <>
          <div className="service-list-actions">
            <button className="add-button" onClick={handleAddService}>
              Add New Service
            </button>
          </div>

          {visibleServices.length === 0 ? (
            <div className="no-services">
              <p>No services have been added yet. Click the button above to get started.</p>
            </div>
          ) : (
            <div className="services-list">
              {Object.keys(visibleByCategory).map(category => (
                <div key={category} className="service-category">
                  <h3>{category}</h3>
                  <div className="service-items">
                    {visibleByCategory[category].map(service => (
                      <div
                        key={service.id}
                        className={`service-item ${!service.isActive ? 'inactive' : ''}`}
                      >
                        <div className="service-content">
                          <div className="service-header">
                            <div className="service-admin-thumbnail">
                              {service.imageUrl
                                ? <img src={service.imageUrl} alt={service.name} />
                                : <div className="service-thumb-placeholder"><span>No Image</span></div>
                              }
                            </div>
                            <div className="service-info">
                              <h4 className="service-name">
                                {service.name}
                                {!service.isActive && <span className="inactive-badge">Inactive</span>}
                              </h4>
                              <p className="service-meta">
                                {service.duration} min • {(() => {
                                  const { priceMin, priceMax } = parseLegacyPrice(service);
                                  return priceMin === priceMax
                                    ? `$${priceMin.toFixed(2)}`
                                    : `$${priceMin.toFixed(2)} – $${priceMax.toFixed(2)}`;
                                })()}
                              </p>
                            </div>
                          </div>

                          {service.description && (
                            <p className="service-description">{service.description}</p>
                          )}

                          {!service.isActive && (
                            <div className="service-inactive">
                              This service is currently inactive and not available for booking.
                            </div>
                          )}
                        </div>

                        <div className="service-actions">
                          <button className="edit-button" onClick={() => handleEditService(service)}>Edit</button>
                          <button
                            className={service.isActive ? "remove-button" : "add-button"}
                            onClick={() => handleToggleActive(service.id)}
                          >
                            {service.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="remove-button" onClick={() => handleDeleteService(service.id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="service-form">
          <h3>{editingServiceId ? 'Edit Service' : 'Add New Service'}</h3>

          <div className="form-group">
            <label htmlFor="name">
              Service Name<span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              className={fieldErrors.name ? 'input-error' : ''}
              required
            />
            {fieldErrors.name && <span className="field-error-message">{fieldErrors.name}</span>}
          </div>

          {/* Service Image Upload */}
          <div className="form-group">
            <label>Service Image</label>
            <div
              {...getImageRootProps()}
              className={`service-image-dropzone${isDragActive ? ' drag-active' : ''}${imageUploading ? ' uploading' : ''}`}
            >
              <input {...getImageInputProps()} />
              {imageUploading ? (
                <div className="service-image-dropzone-placeholder">
                  <span className="upload-icon">⏳</span>
                  <p>Uploading image...</p>
                </div>
              ) : formData.imageUrl ? (
                <div className="service-image-upload-preview">
                  <img src={formData.imageUrl} alt="Service preview" />
                  <div className="service-image-overlay">
                    <p>Drop a new image to replace, or click to select</p>
                    <button type="button" className="remove-image-button" onClick={handleRemoveImage}>
                      Remove Image
                    </button>
                  </div>
                </div>
              ) : (
                <div className="service-image-dropzone-placeholder">
                  <span className="upload-icon">📷</span>
                  <p>{isDragActive ? 'Drop the image here' : 'Drop a service image here, or click to select'}</p>
                  <p className="upload-hint">JPG, PNG, WebP · Recommended: 800 × 500 px</p>
                </div>
              )}
            </div>
            {imageUploadError && <span className="field-error-message">{imageUploadError}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="duration">
                Duration (minutes)<span className="required-asterisk">*</span>
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                className={fieldErrors.duration ? 'input-error' : ''}
                min="1"
                required
              />
              {fieldErrors.duration && <span className="field-error-message">{fieldErrors.duration}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priceMin">
                Min Price ($)
              </label>
              <input
                type="number"
                id="priceMin"
                name="priceMin"
                value={formData.priceMin}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                className={fieldErrors.priceMin ? 'input-error' : ''}
                min="0"
                step="0.01"
              />
              {fieldErrors.priceMin && <span className="field-error-message">{fieldErrors.priceMin}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="priceMax">
                Max Price ($)
              </label>
              <input
                type="number"
                id="priceMax"
                name="priceMax"
                value={formData.priceMax}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                className={fieldErrors.priceMax ? 'input-error' : ''}
                min="0"
                step="0.01"
              />
              {fieldErrors.priceMax && <span className="field-error-message">{fieldErrors.priceMax}</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Describe the service, what's included, and any special features..."
            />
          </div>
          
          {/* Products Used */}
          <div className="form-group">
            <label>Products Used <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: 'var(--text-muted, #888)' }}>(optional — up to 3 · stock depletes when a transaction is completed)</span></label>
            {formData.linkedProducts.map((lp, idx) => (
              <div key={idx} className="form-row" style={{ alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{ flex: 2 }}>
                  <select
                    value={lp.productId}
                    onChange={e => {
                      const pid = e.target.value;
                      const prod = salonProducts.find(p => p.id === pid);
                      setFormData(prev => {
                        const rows = [...prev.linkedProducts];
                        rows[idx] = { productId: pid, productName: prod?.name || '', qty: pid ? (rows[idx].qty || '') : '' };
                        return { ...prev, linkedProducts: rows };
                      });
                    }}
                  >
                    <option value="">— None —</option>
                    {salonProducts
                      .filter(p => p.id === lp.productId || !formData.linkedProducts.some((r, i) => i !== idx && r.productId === p.id))
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    type="number"
                    placeholder="Qty"
                    min="0"
                    max="10"
                    step="0.1"
                    disabled={!lp.productId}
                    value={lp.qty}
                    onChange={e => {
                      const val = e.target.value;
                      setFormData(prev => {
                        const rows = [...prev.linkedProducts];
                        rows[idx] = { ...rows[idx], qty: val };
                        return { ...prev, linkedProducts: rows };
                      });
                    }}
                    style={{ opacity: lp.productId ? 1 : 0.4 }}
                  />
                </div>
                {formData.linkedProducts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => {
                      const rows = prev.linkedProducts.filter((_, i) => i !== idx);
                      return { ...prev, linkedProducts: rows.length ? rows : [{ productId: '', productName: '', qty: '' }] };
                    })}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #c0392b)', fontSize: '1.2em', padding: '0 4px', lineHeight: 1 }}
                    title="Remove"
                  >×</button>
                )}
              </div>
            ))}
            {formData.linkedProducts.length < 3 && (
              <button
                type="button"
                className="add-button"
                style={{ marginTop: '4px', fontSize: '0.85em', padding: '4px 12px' }}
                onClick={() => setFormData(prev => ({
                  ...prev,
                  linkedProducts: [...prev.linkedProducts, { productId: '', productName: '', qty: '' }]
                }))}
              >
                + Add Another Product
              </button>
            )}
            <small style={{ display: 'block', marginTop: 6, color: 'var(--text-muted, #888)' }}>Qty per service (0–10, up to 1 decimal place)</small>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
            />
            <label htmlFor="isActive">
              Service is active and available for booking
            </label>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="allowConcurrentBooking"
              name="allowConcurrentBooking"
              checked={formData.allowConcurrentBooking}
              onChange={handleInputChange}
            />
            <label htmlFor="allowConcurrentBooking">
              Allow multiple services to share this time slot
            </label>
          </div>

          {formData.allowConcurrentBooking && (
            <div className="form-group">
              <label htmlFor="concurrentTimePercentage">
                Shared Time (%)
              </label>
              <input
                type="number"
                id="concurrentTimePercentage"
                name="concurrentTimePercentage"
                value={formData.concurrentTimePercentage}
                disabled
              />
              <div className="form-helper-text">
                <small>% of time slot considered shared when multiple services overlap. Fixed at 50%.</small>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={handleCancelEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="save-button"
              onClick={handleSaveService}
              disabled={loading || imageUploading}
            >
              {loading ? 'Saving...' : imageUploading ? 'Uploading image...' : editingServiceId ? 'Update Service' : 'Add Service'}
            </button>
          </div>
        </div>
      )}

      {cropFile && (
        <ImageCropModal
          file={cropFile}
          kind="service"
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
}

export default ServicesEditor;